import { useEffect, useState, useCallback } from "react";
import api, { eur, formatApiErrorDetail } from "@/lib/api";
import { usePaginated, Paginator } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Search, Download, Printer, Receipt, Ban, Eye, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PAY = { hotovost: "Hotovosť", karta: "Karta", prevod: "Prevod" };
const fmtD = (s) => (s ? s.split("-").reverse().join(".") : "");

async function getPdf(id) {
  const res = await api.get(`/internal-docs/${id}/pdf`, { responseType: "blob" });
  return URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
}

function EditDocDialog({ doc, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (doc) { setF({ with_vat: !!doc.with_vat, payment_method: doc.payment_method || "hotovost", paid_date: doc.paid_date || "", note: doc.note || "" }); } }, [doc]);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/internal-docs/${doc.id}`, f); toast.success("Doklad uložený"); onClose(); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!doc} onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="sm:max-w-md" data-testid="internal-doc-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase">Upraviť doklad {doc?.number}</DialogTitle></DialogHeader>
        {f && (
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Zákazník zaplatil</Label>
              <div className="flex rounded-lg border border-border p-0.5">
                <button type="button" onClick={() => setF({ ...f, with_vat: true })} className={`flex-1 rounded px-3 py-2 text-sm font-semibold ${f.with_vat ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="internal-doc-edit-with-vat">s DPH</button>
                <button type="button" onClick={() => setF({ ...f, with_vat: false })} className={`flex-1 rounded px-3 py-2 text-sm font-semibold ${!f.with_vat ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="internal-doc-edit-without-vat">bez DPH</button>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Spôsob úhrady</Label>
                <Select value={f.payment_method} onValueChange={(v) => setF({ ...f, payment_method: v })}>
                  <SelectTrigger data-testid="internal-doc-edit-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PAY).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Dátum úhrady</Label><Input type="date" value={f.paid_date} onChange={(e) => setF({ ...f, paid_date: e.target.value })} data-testid="internal-doc-edit-paid-date" /></div>
            </div>
            <div className="space-y-1.5"><Label>Poznámka</Label><Textarea rows={3} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="internal-doc-edit-note" /></div>
            <p className="text-xs text-muted-foreground">Položky a ceny sa preberajú zo zákazky.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Zrušiť</Button>
          <Button onClick={save} disabled={busy || !f?.paid_date} className="gap-2" data-testid="internal-doc-edit-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Uložiť</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewDialog({ preview, onClose }) {
  return (
    <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="flex h-[90vh] flex-col sm:max-w-4xl" data-testid="internal-doc-preview-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase">Interný doklad {preview?.number}</DialogTitle></DialogHeader>
        {preview && <iframe title="Náhľad dokladu" src={preview.url} className="w-full flex-1 rounded-lg border border-border bg-white" data-testid="internal-doc-preview-frame" />}
      </DialogContent>
    </Dialog>
  );
}

export default function InternalDocsList() {
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("");
  const [editDoc, setEditDoc] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/internal-docs", { params: { q, month } });
    setDocs(data || []);
  }, [q, month]);
  useEffect(() => { load(); }, [load]);
  const pg = usePaginated(docs);

  const view = async (d) => {
    try { setPreview({ number: d.number, url: await getPdf(d.id) }); }
    catch { toast.error("PDF sa nepodarilo vygenerovať"); }
  };
  const closePreview = () => { if (preview) { URL.revokeObjectURL(preview.url); } setPreview(null); };
  const download = async (d) => {
    try { const url = await getPdf(d.id); const a = document.createElement("a"); a.href = url; a.download = `interny_doklad_${d.number}.pdf`; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch { toast.error("PDF sa nepodarilo vygenerovať"); }
  };
  const print = async (d) => {
    try {
      const url = await getPdf(d.id);
      const f = document.createElement("iframe");
      Object.assign(f.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
      f.src = url;
      f.onload = () => { setTimeout(() => { f.contentWindow.focus(); f.contentWindow.print(); }, 300); };
      document.body.appendChild(f);
      setTimeout(() => { f.remove(); URL.revokeObjectURL(url); }, 60000);
    } catch { toast.error("PDF sa nepodarilo vygenerovať"); }
  };
  const storno = async (d) => {
    try { await api.post(`/internal-docs/${d.id}/storno`); toast.success(`Doklad ${d.number} stornovaný, diely vrátené do skladu`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="internal-docs-list">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="min-h-[48px] pl-9" placeholder="Hľadať číslo, zákazníka, zákazku…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="internal-doc-search" />
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="internal-doc-month" />
        {month && <Button variant="ghost" size="sm" onClick={() => setMonth("")} data-testid="internal-doc-month-clear">Všetky mesiace</Button>}
      </div>
      <div className="space-y-2">
        {pg.paged.map((d) => (
          <div key={d.id} className={`card-surface flex flex-wrap items-center justify-between gap-3 p-4 ${d.status === "storno" ? "opacity-60" : ""}`} data-testid={`internal-doc-row-${d.id}`}>
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-violet-500/15"><Receipt className="h-5 w-5 text-violet-300" /></div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-heading text-lg font-bold">{d.number}</span>
                  <span className="rounded bg-violet-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-violet-300">Interný doklad</span>
                  {d.status === "storno" && <span className="rounded bg-red-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-red-400" data-testid={`internal-doc-storno-badge-${d.id}`}>Stornovaný</span>}
                </div>
                <div className="text-sm text-muted-foreground">{d.customer?.name || "—"} · {d.job_number} {d.job_title} · {fmtD(d.paid_date)} · {PAY[d.payment_method] || d.payment_method}</div>
                {d.note && <div className="text-xs text-muted-foreground/80">{d.note}</div>}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <span className="mr-1 font-mono text-lg font-bold text-primary">{eur(d.total)} <span className="text-xs font-normal text-muted-foreground">{d.with_vat && d.vat_total > 0 ? "s DPH" : "bez DPH"}</span></span>
              <Button variant="ghost" size="icon" onClick={() => view(d)} title="Náhľad" data-testid={`internal-doc-view-${d.id}`}><Eye className="h-4 w-4" /></Button>
              {d.status !== "storno" && <Button variant="ghost" size="icon" onClick={() => setEditDoc(d)} title="Upraviť" data-testid={`internal-doc-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>}
              <Button variant="ghost" size="icon" onClick={() => print(d)} title="Tlačiť" data-testid={`internal-doc-print-${d.id}`}><Printer className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => download(d)} title="Stiahnuť PDF" data-testid={`internal-doc-pdf-${d.id}`}><Download className="h-4 w-4" /></Button>
              {d.status !== "storno" && (
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="ghost" size="icon" title="Stornovať" data-testid={`internal-doc-storno-${d.id}`}><Ban className="h-4 w-4 text-destructive" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Stornovať doklad {d.number}?</AlertDialogTitle><AlertDialogDescription>Diely sa vrátia do skladu a tržba sa odpočíta. Zákazka ostane bez dokladu.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Zrušiť</AlertDialogCancel><AlertDialogAction onClick={() => storno(d)} data-testid={`internal-doc-storno-confirm-${d.id}`}>Stornovať</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        ))}
        {!docs.length && <div className="py-12 text-center text-muted-foreground" data-testid="internal-docs-empty">Žiadne interné doklady.</div>}
      </div>
      <Paginator {...pg} />
      <EditDocDialog doc={editDoc} onClose={() => setEditDoc(null)} onSaved={load} />
      <PreviewDialog preview={preview} onClose={closePreview} />
    </div>
  );
}
